import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import RightSheet from "../ui-components/RightSheet";
import Modal from "../ui-components/Modal";
import FormButton from "../form-components/FormButton";
import ScopeGroupCard from "./ScopeGroupCard";
import OrSeparator from "./OrSeparator";
import {
  createEmptyClause,
  createEmptyGroup,
  isScopeValid,
  findFirstInvalid,
  apiToDraft,
  draftToApi,
} from "./scopeMapping";

// Pre-resolve icons at build time (the macro requires string literals).
const STARTER_ICONS = {
  sitemap: icon({ name: "sitemap", style: "regular" }),
  box: icon({ name: "box", style: "regular" }),
  lightbulb: icon({ name: "lightbulb", style: "regular" }),
  coins: icon({ name: "coins", style: "regular" }),
  filter: icon({ name: "filter", style: "regular" }),
};

/**
 * Hardcoded dimension hints for the starter cards.
 * We match against dimension name (case-insensitive) to pick a hint;
 * fallback shows the dimension name with a generic hint.
 */
const DIMENSION_HINTS = {
  organization: {
    hint: "Teams, departments, cost centers",
    iconKey: "sitemap",
  },
  product: {
    hint: "Products or services you deliver",
    iconKey: "box",
  },
  "use case": {
    hint: "Workloads, features, or initiatives",
    iconKey: "lightbulb",
  },
  "financial category": {
    hint: "Spend categories like AI, compute, storage",
    iconKey: "coins",
  },
};

const getDimensionHint = (dimName) => {
  const lower = (dimName || "").toLowerCase();
  for (const [key, val] of Object.entries(DIMENSION_HINTS)) {
    if (lower.includes(key)) return val;
  }
  return { hint: `Filter by ${dimName}`, iconKey: "filter" };
};

/**
 * BudgetScopeSheet — right-side scope builder sidebar with group support.
 *
 * Contains:
 *  - Dimension starters (when draft has no groups)
 *  - Group cards with clause editors (AND within group, OR between groups)
 *  - "Add another group" button
 *  - Apply / Cancel footer with dirty-check discard confirmation
 */
const BudgetScopeSheet = ({
  open,
  savedScope = { scope_mode: "include", clauses: [] },
  onApply,
  onCancel,
  dimensions = [],
}) => {
  // ── Draft state ─────────────────────────────────────────────

  const [scopeDraft, setScopeDraft] = useState({ groups: [] });
  const [activeClauseId, setActiveClauseId] = useState(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // Reset draft when sheet opens
  useEffect(() => {
    if (open) {
      setScopeDraft(apiToDraft(savedScope));
      setActiveClauseId(null);
      setShowDiscardModal(false);
      setValidationError(null);
    }
  }, [open, savedScope]);

  // ── Derived state ───────────────────────────────────────────

  const isTotal = !!scopeDraft.total;
  const draftIsEmpty = scopeDraft.groups.length === 0;

  const isDirty = useMemo(() => {
    const currentApi = draftToApi(scopeDraft, savedScope?.scope_mode ?? "include");
    const savedMode = savedScope?.scope_mode ?? "include";
    // A change in scope_mode (e.g. include ↔ total) is always a dirty change.
    if (currentApi.scope_mode !== savedMode) return true;
    const savedClauses = Array.isArray(savedScope?.clauses)
      ? savedScope.clauses
      : [];
    if (currentApi.clauses.length !== savedClauses.length) return true;
    return JSON.stringify(currentApi.clauses) !== JSON.stringify(savedClauses);
  }, [scopeDraft, savedScope]);

  const scopeIsValid = useMemo(() => isScopeValid(scopeDraft), [scopeDraft]);

  const subtitle = isTotal
    ? "This budget covers all cloud & AI spend."
    : draftIsEmpty
    ? "Select at least one condition. You can refine this later."
    : "Update conditions and apply to save changes.";

  // ── Scope type toggle (Scoped ↔ Total) ──────────────────────

  const handleSelectScoped = useCallback(() => {
    setScopeDraft((prev) => ({ ...prev, total: false }));
    setValidationError(null);
  }, []);

  const handleSelectTotal = useCallback(() => {
    setScopeDraft({ total: true, groups: [] });
    setActiveClauseId(null);
    setValidationError(null);
  }, []);

  // ── Group operations ────────────────────────────────────────

  const handleAddGroup = useCallback(() => {
    const newGroup = createEmptyGroup();
    setScopeDraft((prev) => ({
      ...prev,
      groups: [...prev.groups, newGroup],
    }));
    // Auto-expand the first clause in the new group
    setActiveClauseId(newGroup.clauses[0].id);
    setValidationError(null);
  }, []);

  const handleRemoveGroup = useCallback(
    (groupId) => {
      setScopeDraft((prev) => {
        const newGroups = prev.groups.filter((g) => g.id !== groupId);
        return { ...prev, groups: newGroups };
      });
      // If active clause was in the removed group, clear it
      setScopeDraft((prev) => {
        const allClauseIds = prev.groups.flatMap((g) =>
          g.clauses.map((c) => c.id)
        );
        if (activeClauseId && !allClauseIds.includes(activeClauseId)) {
          setActiveClauseId(null);
        }
        return prev;
      });
      setValidationError(null);
    },
    [activeClauseId]
  );

  // ── Clause operations (within a group) ──────────────────────

  const handleAddClause = useCallback((groupId) => {
    const newClause = createEmptyClause();
    setScopeDraft((prev) => ({
      ...prev,
      groups: prev.groups.map((g) =>
        g.id === groupId
          ? { ...g, clauses: [...g.clauses, newClause] }
          : g
      ),
    }));
    setActiveClauseId(newClause.id);
    setValidationError(null);
  }, []);

  const handleRemoveClause = useCallback(
    (groupId, clauseId) => {
      setScopeDraft((prev) => ({
        ...prev,
        groups: prev.groups.map((g) => {
          if (g.id !== groupId) return g;
          const remaining = g.clauses.filter((c) => c.id !== clauseId);
          // If group becomes empty, auto-add one blank clause
          if (remaining.length === 0) {
            return { ...g, clauses: [createEmptyClause()] };
          }
          return { ...g, clauses: remaining };
        }),
      }));
      if (activeClauseId === clauseId) {
        setActiveClauseId(null);
      }
      setValidationError(null);
    },
    [activeClauseId]
  );

  const handleUpdateClause = useCallback((groupId, clauseId, updatedClause) => {
    setScopeDraft((prev) => ({
      ...prev,
      groups: prev.groups.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          clauses: g.clauses.map((c) =>
            c.id === clauseId ? { ...c, ...updatedClause, id: clauseId } : c
          ),
        };
      }),
    }));
    setValidationError(null);
  }, []);

  // ── Dimension starter click ─────────────────────────────────

  const handleStarterClick = useCallback((dimensionId) => {
    const newClause = createEmptyClause();
    newClause.dimension_id = dimensionId;
    const newGroup = {
      ...createEmptyGroup(),
      clauses: [newClause],
    };
    setScopeDraft({ groups: [newGroup] });
    setActiveClauseId(newClause.id);
    setValidationError(null);
  }, []);

  // ── Apply / Close ───────────────────────────────────────────

  const handleApply = useCallback(() => {
    // Total scope is always valid — no clauses to complete.
    if (isTotal) {
      onApply(draftToApi(scopeDraft));
      setValidationError(null);
      return;
    }

    if (!scopeIsValid) {
      const invalid = findFirstInvalid(scopeDraft);
      if (invalid) {
        setActiveClauseId(invalid.clauseId);
      }
      setValidationError("Complete all conditions before applying.");
      return;
    }

    const payload = draftToApi(scopeDraft, savedScope?.scope_mode ?? "include");
    onApply(payload);
    setValidationError(null);
  }, [isTotal, scopeDraft, scopeIsValid, savedScope, onApply]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      onCancel();
    }
  }, [isDirty, onCancel]);

  const handleDiscardConfirm = useCallback(() => {
    setShowDiscardModal(false);
    onCancel();
  }, [onCancel]);

  // ── Render ──────────────────────────────────────────────────

  const footer = (
    <div>
      {validationError && (
        <p className="text-xs text-danger mb-2">{validationError}</p>
      )}
      <p className="text-xs text-text-2 mb-3">
        {isTotal
          ? "This budget covers all cloud & AI spend."
          : "This budget will include all matching costs."}
      </p>
      <div className="flex items-center justify-between">
        <FormButton title="Cancel" style="tertiary" onClick={handleClose} />
        <FormButton
          title="Apply scope"
          style="primary"
          onClick={handleApply}
          disabled={!isTotal && !draftIsEmpty && !scopeIsValid}
        />
      </div>
    </div>
  );

  return (
    <>
      <RightSheet
        open={open}
        onClose={handleClose}
        title="Define budget scope"
        subtitle={subtitle}
        footer={footer}
      >
        {/* Scope type toggle — Scoped vs Total */}
        <div className="mb-5">
          <div
            className="grid grid-cols-2 gap-1 p-1 bg-surface-2 rounded-seg"
            role="radiogroup"
            aria-label="Budget scope type"
          >
            <button
              type="button"
              role="radio"
              aria-checked={!isTotal}
              onClick={handleSelectScoped}
              className={`px-3 py-2 text-sm font-medium rounded-tab transition-colors duration-150 cursor-pointer ${
                !isTotal
                  ? "bg-primary-soft-strong text-primary font-semibold shadow-pill"
                  : "text-text-2 hover:text-text"
              }`}
            >
              Scoped
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={isTotal}
              onClick={handleSelectTotal}
              className={`px-3 py-2 text-sm font-medium rounded-tab transition-colors duration-150 cursor-pointer ${
                isTotal
                  ? "bg-primary-soft-strong text-primary font-semibold shadow-pill"
                  : "text-text-2 hover:text-text"
              }`}
            >
              Total — all cloud &amp; AI spend
            </button>
          </div>
        </div>

        {/* Total scope explainer */}
        {isTotal && (
          <div className="flex items-start gap-3 p-4 rounded-card border border-card-bd bg-surface-2">
            <div className="w-8 h-8 pt-2 rounded-ctrl bg-card flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon
                icon={icon({ name: "globe", style: "regular" })}
                className="text-text-2 text-sm"
              />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-text">
                Total budget
              </p>
              <p className="text-xs text-text-2 mt-0.5">
                This budget covers all cloud &amp; AI spend.
              </p>
            </div>
          </div>
        )}

        {/* Dimension starters — shown when draft has no groups */}
        {!isTotal && draftIsEmpty && (
          <div>
            <p className="text-sm font-medium text-text mb-4">
              Choose what this budget is based on
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {dimensions.map((dim) => {
                const { hint, iconKey } = getDimensionHint(dim.name);
                const faIcon = STARTER_ICONS[iconKey] || STARTER_ICONS.filter;
                return (
                  <button
                    key={dim.id}
                    onClick={() => handleStarterClick(dim.id)}
                    className="flex items-start gap-3 p-4 rounded-card border border-card-bd hover:border-primary hover:bg-primary-soft transition-colors duration-150 text-left cursor-pointer group"
                  >
                    <div className="w-8 h-8 pt-2 rounded-ctrl bg-surface-2 group-hover:bg-primary-soft flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon
                        icon={faIcon}
                        className="text-text-2 group-hover:text-primary text-sm"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text">
                        {dim.name}
                      </p>
                      <p className="text-xs text-text-2 mt-0.5">{hint}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {dimensions.length === 0 && (
              <div className="text-center py-8">
                <p className="text-sm text-text-2">
                  No dimensions available. Configure cost model dimensions
                  first.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Group cards — shown when draft has groups */}
        {!isTotal && !draftIsEmpty && (
          <div className="space-y-1">
            {scopeDraft.groups.map((group, gIdx) => (
              <React.Fragment key={group.id}>
                {gIdx > 0 && <OrSeparator />}
                <ScopeGroupCard
                  group={group}
                  groupIndex={gIdx}
                  dimensions={dimensions}
                  activeClauseId={activeClauseId}
                  setActiveClauseId={setActiveClauseId}
                  showRemoveGroup={scopeDraft.groups.length > 1}
                  onUpdateClause={(clauseId, updated) =>
                    handleUpdateClause(group.id, clauseId, updated)
                  }
                  onAddClause={() => handleAddClause(group.id)}
                  onRemoveClause={(clauseId) =>
                    handleRemoveClause(group.id, clauseId)
                  }
                  onRemoveGroup={() => handleRemoveGroup(group.id)}
                />
              </React.Fragment>
            ))}

            {/* Add another group */}
            <div className="pt-3">
              <FormButton
                title="Add another group"
                style="secondary"
                onClick={handleAddGroup}
                icon={
                  <FontAwesomeIcon
                    icon={icon({ name: "plus", style: "solid" })}
                  />
                }
                className="w-full"
              />
              <p className="text-xs text-text-2 text-center mt-1.5">
                Create an alternative set of conditions
              </p>
            </div>
          </div>
        )}
      </RightSheet>

      {/* Discard confirmation modal */}
      <Modal
        show={showDiscardModal}
        close={() => setShowDiscardModal(false)}
        title="Discard changes?"
        description="You have unsaved scope changes. Discard them?"
        confirmButton="Discard"
        confirmAction={handleDiscardConfirm}
        cancelButton="Keep editing"
        alertIcon
      />
    </>
  );
};

BudgetScopeSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  savedScope: PropTypes.shape({
    scope_mode: PropTypes.string,
    clauses: PropTypes.array,
  }),
  onApply: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  dimensions: PropTypes.array,
};

export default BudgetScopeSheet;
